import { useState } from 'react';
import { Utensils, Beer, Coffee, Wine } from 'lucide-react';

const MenuSection = ({ 
  title, 
  items 
}: { 
  title: string; 
  items: { name: string; description: string; price: string; highlight?: boolean; }[] 
}) => (
  <div className="mb-12">
    <h3 className="heading-sm mb-6 text-barbecue-600">{title}</h3>
    <div className="space-y-1">
      {items.map((item, index) => (
        <div key={index} className="menu-item">
          <div>
            <div className="flex items-center">
              <h4 className={`font-medium ${item.highlight ? 'text-barbecue-600' : ''}`}>
                {item.name}
              </h4>
              {item.highlight && (
                <span className="ml-2 bg-barbecue-100 text-barbecue-800 text-xs px-2 py-0.5 rounded">
                  Priporočamo
                </span>
              )}
            </div>
            <p className="text-sm text-foreground/70 mt-1">{item.description}</p>
          </div>
          <div className="font-medium whitespace-nowrap ml-4">{item.price} €</div>
        </div>
      ))}
    </div>
  </div>
);

const Menu = () => {
  const [activeTab, setActiveTab] = useState('food');

  const foodMenuItems = {
    starters: [
      { name: 'Domači kruh z namazi', description: 'Hišni kruh s tremi različnimi namazi', price: '6.50' },
      { name: 'Goveja juha z rezanci', description: 'Tradicionalna slovenska goveja juha', price: '4.50' },
      { name: 'Narezek domačih mesnin', description: 'Izbor lokalnih suhomesnatih izdelkov', price: '9.80', highlight: true },
    ],
    bbq: [
      { name: 'Mešano meso z žara', description: 'Čevapčiči, pleskavica, svinjski vrat, piščančji file', price: '15.90', highlight: true },
      { name: 'Pleskavica polnjena s sirom', description: 'Sočna pleskavica z mladim sirom', price: '11.50' },
      { name: 'BBQ rebra', description: 'Marinirana svinjska rebra s prilogo', price: '14.80' },
      { name: 'Piščančji ražnjiči', description: 'Mariniran piščanec z zelenjavo', price: '12.50' },
    ],
    sides: [
      { name: 'Pražen krompir', description: 'Tradicionalno pripravljen', price: '3.50' },
      { name: 'Ajvar in čebula', description: 'Domač ajvar s svežo čebulo', price: '2.50' },
      { name: 'Mešana solata', description: 'Sveža sezonska zelenjava', price: '3.90' },
    ],
    desserts: [
      { name: 'Potica', description: 'Tradicionalna slovenska orehova potica', price: '4.50', highlight: true },
      { name: 'Jabolčni zavitek', description: 'S cimetom in vaniljevo kremo', price: '4.90' },
      { name: 'Čokoladna torta', description: 'Z jagodnim prelivom', price: '5.20' },
    ],
  };

  const drinksMenuItems = {
    beers: [
      { name: 'Laško', description: 'Točeno 0.5l', price: '3.20' },
      { name: 'Union', description: 'Točeno 0.5l', price: '3.20' },
      { name: 'Lokalno craft pivo', description: 'Steklenica 0.33l', price: '4.50', highlight: true },
    ],
    wines: [
      { name: 'Hiša Malec', description: 'Belo vino - kozarec', price: '3.50' },
      { name: 'Cviček', description: 'Kozarec', price: '2.80' },
      { name: 'Teran', description: 'Kozarec', price: '3.50' },
      { name: 'Refošk', description: 'Steklenica 0.75l', price: '18.00', highlight: true },
    ],
    spirits: [
      { name: 'Domači borovničevec', description: '0.03l', price: '2.50', highlight: true },
      { name: 'Viljamovka', description: '0.03l', price: '2.80' },
      { name: 'Whiskey Jack Daniels', description: '0.03l', price: '3.50' },
    ],
    soft: [
      { name: 'Domač ledeni čaj', description: '0.3l', price: '2.80' },
      { name: 'Naravni sok', description: 'Jabolko ali pomaranča 0.3l', price: '2.50' },
      { name: 'Kava', description: 'Espresso', price: '1.50' },
      { name: 'Cappuccino', description: '', price: '2.20' },
    ],
  };

  return (
    <section className="section-container" id="menu">
      <div className="text-center max-w-3xl mx-auto mb-12 animate-fade-in">
        <div className="inline-flex items-center space-x-2 mb-4">
          <div className="h-px w-8 bg-barbecue-500"></div>
          <span className="text-barbecue-500 font-medium">NAŠ MENI</span>
          <div className="h-px w-8 bg-barbecue-500"></div>
        </div>
        <h2 className="heading-lg mb-4">Kulinarične specialitete in izbrana pijača</h2>
        <p className="paragraph">
          Odkrijte naše tradicionalne slovenske jedi, pripravljene na žaru po 
          receptih, ki se prenašajo iz roda v rod. Ponudbo dopolnjuje skrbno izbrana 
          kolekcija pijač.
        </p>
      </div>

      {/* Menu Tabs */}
      <div className="flex justify-center mb-12">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            onClick={() => setActiveTab('food')}
            className={`inline-flex items-center px-6 py-3 rounded-l-lg ${
              activeTab === 'food'
                ? 'bg-barbecue-600 text-white'
                : 'bg-white text-foreground hover:bg-barbecue-50'
            } transition-colors duration-300`}
          >
            <Utensils className="mr-2 h-5 w-5" />
            Hrana
          </button>
          <button
            onClick={() => setActiveTab('drinks')}
            className={`inline-flex items-center px-6 py-3 rounded-r-lg ${
              activeTab === 'drinks'
                ? 'bg-barbecue-600 text-white'
                : 'bg-white text-foreground hover:bg-barbecue-50'
            } transition-colors duration-300`}
          >
            <Beer className="mr-2 h-5 w-5" />
            Pijača
          </button>
        </div>
      </div>

      {/* Food Menu */}
      <div 
        className={`transition-all duration-500 transform ${
          activeTab === 'food' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 hidden'
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <MenuSection title="Predjedi" items={foodMenuItems.starters} />
            <MenuSection title="Jedi z žara" items={foodMenuItems.bbq} />
          </div>
          <div>
            <MenuSection title="Priloge" items={foodMenuItems.sides} />
            <MenuSection title="Sladice" items={foodMenuItems.desserts} />
          </div>
        </div>
      </div>

      {/* Drinks Menu */}
      <div 
        className={`transition-all duration-500 transform ${
          activeTab === 'drinks' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 hidden'
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center mb-6">
              <Beer className="text-barbecue-600 mr-2" />
              <h3 className="heading-sm text-barbecue-600">Pivo</h3>
            </div>
            <MenuSection title="" items={drinksMenuItems.beers} />
            
            <div className="flex items-center mb-6">
              <Wine className="text-barbecue-600 mr-2" />
              <h3 className="heading-sm text-barbecue-600">Vino</h3>
            </div>
            <MenuSection title="" items={drinksMenuItems.wines} />
          </div>
          <div>
            <div className="flex items-center mb-6">
              <Wine className="text-barbecue-600 mr-2" />
              <h3 className="heading-sm text-barbecue-600">Žgane pijače</h3>
            </div>
            <MenuSection title="" items={drinksMenuItems.spirits} />
            
            <div className="flex items-center mb-6">
              <Coffee className="text-barbecue-600 mr-2" />
              <h3 className="heading-sm text-barbecue-600">Brezalkoholne pijače</h3>
            </div>
            <MenuSection title="" items={drinksMenuItems.soft} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Menu;